import React, { useState } from 'react';
import axios from 'axios';
import { Button, Form, Alert, Spinner, ListGroup, Badge, Card, Accordion } from 'react-bootstrap';
import BASE_URL from '../config'; // Import your BASE_URL


const DeleteEmail = () => {
  const [email, setEmail] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [invalidEmails, setInvalidEmails] = useState([]);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResults([]);
    setInvalidEmails([]);
    setProcessedCount(0);
    
    try {
      const response = await axios.get(`${BASE_URL}api/proxy/delete-email`, {
        params: { email }
      });
      
      // Handle the new response structure
      const { success: isSuccess, processedEmails, emailResults, invalidEmails: invalidList, warning } = response.data;
      
      setResults(emailResults || []);
      setProcessedCount(processedEmails || 0);
      setInvalidEmails(invalidList || []);
      
      if (isSuccess) {
        setSuccess(`Successfully processed deletion for ${processedEmails} email(s)`);
      } else {
        setSuccess(`No deletions were needed for the provided email(s)`);
      }

      // Show warning if there were invalid emails
      if (warning) {
        setError(warning);
      }
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete email');
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'deleted':
      case 'added':
        return 'success';
      case 'not_found':
      case 'exists':
        return 'secondary';
      case 'error':
        return 'danger';
      default:
        return 'light';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'deleted': 'danger',
      'added': 'success',
      'not_found': 'secondary',
      'exists': 'info',
      'error': 'warning'
    };
    return badges[status] || 'light';
  };

  // Count found results for each email
  const getFoundCount = (emailResult) => {
    return emailResult.results.filter(r => r.found).length;
  };

  return (
    <div className="container mt-4">
      <div className="card shadow-lg">
        <div className="card-body">
          <h2 className="text-center mb-4">Delete Email from Databases</h2>
          
          <Form onSubmit={handleDelete} className="mb-4">
            <Form.Group controlId="emailDelete" className="mb-3">
              <Form.Label>Enter Email Address(es) to Delete:</Form.Label>
              <Form.Text className="d-block mb-2 text-muted">
                Separate multiple emails with commas (e.g., test@mail.com, test1@mail.com)
              </Form.Text>
              <div className="d-flex">
                <Form.Control
                  type="text"
                  placeholder="Enter email(s) to delete"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Button 
                  variant="danger" 
                  type="submit" 
                  className="ms-2" 
                  disabled={loading}
                  style={{ minWidth: '100px' }}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : 'Delete'}
                </Button>
              </div>
            </Form.Group>
          </Form>

          {error && <Alert variant="warning" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          {invalidEmails.length > 0 && (
            <Alert variant="warning">
              <strong>Invalid Emails:</strong> {invalidEmails.join(', ')}
            </Alert>
          )}

          {results.length > 0 && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Deletion Results</h5>
                <Badge bg="info">{processedCount} Email(s) Processed</Badge>
              </div>

              {results.length === 1 ? (
                // Single email - show flat list
                <div>
                  <Card className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>{results[0].email}</strong>
                      {results[0].foundAnywhere ? (
                        <Badge bg="success">Actions Taken</Badge>
                      ) : (
                        <Badge bg="secondary">Not Found</Badge>
                      )}
                    </Card.Header>
                  </Card>
                  
                  <ListGroup>
                    {results[0].results.map((result, index) => (
                      <ListGroup.Item 
                        key={index} 
                        variant={getStatusStyle(result.status)}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <div>
                          <strong>{result.database}.{result.table}</strong>
                          <div className="text-muted small">{result.message}</div>
                        </div>
                        <Badge bg={getStatusBadge(result.status)}>
                          {result.status}
                        </Badge>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              ) : (
                // Multiple emails - use accordion
                <Accordion defaultActiveKey="0">
                  {results.map((emailResult, emailIndex) => {
                    const foundCount = getFoundCount(emailResult);
                    
                    return (
                      <Accordion.Item eventKey={emailIndex.toString()} key={emailIndex}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                            <span><strong>{emailResult.email}</strong></span>
                            <div className="d-flex gap-2">
                              {emailResult.foundAnywhere ? (
                                <Badge bg="success">{foundCount} Action(s) Taken</Badge>
                              ) : (
                                <Badge bg="secondary">Not Found</Badge>
                              )}
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <ListGroup variant="flush">
                            {emailResult.results.map((result, index) => (
                              <ListGroup.Item 
                                key={index} 
                                variant={getStatusStyle(result.status)}
                                className="d-flex justify-content-between align-items-start"
                              >
                                <div>
                                  <strong>{result.database}.{result.table}</strong>
                                  <div className="text-muted small">{result.message}</div>
                                </div>
                                <Badge bg={getStatusBadge(result.status)}>
                                  {result.status}
                                </Badge>
                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteEmail;